import { useMemo } from "react";
import { format } from "date-fns";

interface AuditUser {
  id?: string | number;
  firstName?: string | null;
  lastName?: string | null;
}

interface AuditInfoProps {
  createdAt?: string | Date | null;
  createdBy?: AuditUser | null;
  updatedAt?: string | Date | null;
  updatedBy?: AuditUser | null;
  className?: string;
}

function formatUserName(user: AuditUser | null | undefined): string {
  if (!user) return "Unknown";
  const firstName = user.firstName || "";
  const lastName = user.lastName || "";
  if (firstName && lastName) {
    return `${firstName} ${lastName.charAt(0)}.`;
  }
  if (firstName) return firstName;
  if (lastName) return lastName;
  return "Unknown";
}

function formatLocalDateTime(date: Date): string {
  return format(date, "MMM d, yyyy 'at' h:mm a");
}

export default function AuditInfo({
  createdAt,
  createdBy,
  updatedAt,
  updatedBy,
  className = "",
}: AuditInfoProps) {
  const { createdText, modifiedText, showModified } = useMemo(() => {
    const createdDate = createdAt ? new Date(createdAt) : null;
    const updatedDate = updatedAt ? new Date(updatedAt) : null;
    
    let createdText = "";
    if (createdDate) {
      const creatorName = formatUserName(createdBy);
      const createdTimeStr = formatLocalDateTime(createdDate);
      createdText = `Created ${createdTimeStr} by ${creatorName}`;
    }
    
    let modifiedText = "";
    let showModified = false;
    
    if (updatedDate && createdDate) {
      const timeDiff = Math.abs(updatedDate.getTime() - createdDate.getTime());
      const oneMinute = 60 * 1000;
      
      if (timeDiff > oneMinute) {
        showModified = true;
        const modifierName = formatUserName(updatedBy);
        const modifiedTimeStr = formatLocalDateTime(updatedDate);
        modifiedText = `Modified ${modifiedTimeStr} by ${modifierName}`;
      }
    } else if (updatedDate) {
      showModified = true;
      const modifierName = formatUserName(updatedBy);
      const modifiedTimeStr = formatLocalDateTime(updatedDate);
      modifiedText = `Modified ${modifiedTimeStr} by ${modifierName}`;
    }
    
    return { createdText, modifiedText, showModified };
  }, [createdAt, createdBy, updatedAt, updatedBy]);

  if (!createdText && !modifiedText) {
    return null;
  }

  return (
    <div className={`text-xs text-muted-foreground ${className}`}>
      {createdText}
      {showModified && (
        <>
          <span className="mx-1.5">•</span>
          {modifiedText}
        </>
      )}
    </div>
  );
}
